import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from '../contexts/SettingsContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as BiIcons from 'react-icons/bi';
import LocalLLMManager from './LocalLLMManager';

const { FiSettings, FiX, FiEye, FiEyeOff, FiCpu } = FiIcons;
const { BiBot } = BiIcons;

const SettingsModal = ({ onClose }) => {
  const { settings, updateSettings } = useSettings();
  const [localSettings, setLocalSettings] = useState(settings);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showLocalLLM, setShowLocalLLM] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = () => {
    updateSettings(localSettings);
    onClose();
  };

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          className="bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center">
              <SafeIcon icon={FiSettings} className="mr-2" />
              Settings
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* AI Configuration */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">AI Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    AI Provider
                  </label>
                  <select
                    value={localSettings.aiProvider}
                    onChange={(e) => handleChange('aiProvider', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-400 focus:outline-none text-white"
                  >
                    <option value="openai">OpenAI (GPT)</option>
                    <option value="anthropic">Anthropic (Claude)</option>
                    <option value="local">Local LLM (Browser)</option>
                    <option value="custom">Custom API</option>
                  </select>
                </div>

                {/* Local LLM Section */}
                {localSettings.aiProvider === 'local' && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center">
                        <SafeIcon icon={BiBot} className="mr-2 text-purple-400" />
                        <span className="text-purple-300 font-medium">Local AI Models</span>
                      </div>
                      <button
                        onClick={() => setShowLocalLLM(true)}
                        className="flex items-center px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        <SafeIcon icon={FiCpu} className="mr-1 text-xs" />
                        Manage Models
                      </button>
                    </div>
                    <p className="text-purple-200 text-sm mb-2">
                      Run AI models directly in your browser for complete privacy and offline usage.
                    </p>
                    {localSettings.localModelId && (
                      <div className="text-sm">
                        <span className="text-gray-400">Active Model: </span>
                        <span className="text-green-400 font-medium">{localSettings.localModelId}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Cloud API Configuration */}
                {(localSettings.aiProvider === 'openai' || localSettings.aiProvider === 'anthropic' || localSettings.aiProvider === 'custom') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={localSettings.aiApiKey}
                          onChange={(e) => handleChange('aiApiKey', e.target.value)}
                          placeholder="Enter your API key"
                          className="w-full px-3 py-2 pr-10 bg-gray-700 border border-gray-600 rounded focus:border-blue-400 focus:outline-none text-white"
                        />
                        <button
                          type="button"
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        >
                          <SafeIcon icon={showApiKey ? FiEyeOff : FiEye} className="text-sm" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Your API key is stored locally in your browser
                      </p>
                    </div>

                    {localSettings.aiProvider === 'custom' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Custom API URL
                        </label>
                        <input
                          type="url"
                          value={localSettings.customApiUrl}
                          onChange={(e) => handleChange('customApiUrl', e.target.value)}
                          placeholder="https://your-api-endpoint.com/chat"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-400 focus:outline-none text-white"
                        />
                      </div>
                    )}

                    {localSettings.aiProvider === 'openai' && (
                      <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-200 text-sm">
                          <strong>OpenAI API:</strong> Get your API key from{' '}
                          <a 
                            href="https://platform.openai.com/api-keys" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-400 underline hover:text-blue-300"
                          >
                            platform.openai.com
                          </a>
                        </p>
                      </div>
                    )}

                    {localSettings.aiProvider === 'anthropic' && (
                      <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                        <p className="text-orange-200 text-sm">
                          <strong>Anthropic API:</strong> Get your API key from{' '}
                          <a 
                            href="https://console.anthropic.com/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-orange-400 underline hover:text-orange-300"
                          >
                            console.anthropic.com
                          </a>
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Editor Settings */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Editor Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Theme
                  </label>
                  <select
                    value={localSettings.theme}
                    onChange={(e) => handleChange('theme', e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-blue-400 focus:outline-none text-white"
                  >
                    <option value="dark">Dark</option>
                    <option value="light">Light</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Font Size
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="24"
                    value={localSettings.fontSize}
                    onChange={(e) => handleChange('fontSize', parseInt(e.target.value))}
                    className="w-full"
                  />
                  <div className="text-sm text-gray-400 mt-1">
                    {localSettings.fontSize}px
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={localSettings.autoSave}
                      onChange={(e) => handleChange('autoSave', e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-300">Auto-save changes</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Privacy & Performance */}
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Privacy & Performance</h3>
              <div className="space-y-3">
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-green-200 text-sm">
                    <strong>Local LLM Benefits:</strong> Complete privacy, no internet required, no API costs, instant responses
                  </p>
                </div>
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-200 text-sm">
                    <strong>Cloud AI Benefits:</strong> More powerful models, latest features, no local storage requirements
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-700 flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded transition-colors text-white"
            >
              Save Settings
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 rounded transition-colors text-white"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </div>

      {/* Local LLM Manager Modal */}
      {showLocalLLM && (
        <LocalLLMManager onClose={() => setShowLocalLLM(false)} />
      )}
    </>
  );
};

export default SettingsModal;